import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fetchBlogs } from "../../../api/blogsAPI";
import { placeholderImage, isValidUrl } from "../../../components/invalidImage";
import Header2 from "../../commons/Header2";
import Footer from "../../commons/Footer";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";

const stripHtml = (h) => h.replace(/<[^>]*>/g, "");
const previewText = (html, n = 140) => {
  const text = stripHtml(DOMPurify.sanitize(html));
  return text.length > n ? text.slice(0, n) + "..." : text;
};

const BlogsListPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    const getBlogs = async () => {
      try {
        const blogsData = await fetchBlogs();
        setBlogs(blogsData);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    getBlogs();
  }, []);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("id-ID", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });

  const filteredBlogs = blogs
    .filter((b) => b.title.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      const t1 = new Date(a.created_at).getTime();
      const t2 = new Date(b.created_at).getTime();
      return sortOrder === "asc" ? t1 - t2 : t2 - t1;
    });

  const featured = filteredBlogs[0];
  const rest = filteredBlogs.slice(1);

  return (
    <div className="relative bg-slate-50 min-h-screen flex flex-col">
      <Header2 />

      {/* Hero / Page header */}
      <section className="relative pt-28 pb-12 lg:pt-36 lg:pb-16 overflow-hidden bg-gradient-to-br from-brand-50 via-white to-eco-50">
        <div className="absolute -top-20 left-1/4 w-72 h-72 bg-brand-200/40 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 right-1/4 w-72 h-72 bg-eco-200/40 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-white text-brand-700 border border-brand-100 shadow-soft mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
              Defra Blog
            </span>
            <h1 className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight mb-4">
              Wawasan Terbaru tentang{" "}
              <span className="text-gradient">Air & Lingkungan</span>
            </h1>
            <p className="font-overpass text-base lg:text-lg text-slate-600">
              Jelajahi artikel pilihan tentang teknologi pengolahan limbah,
              regulasi lingkungan, dan praktik berkelanjutan.
            </p>
          </motion.div>

          {/* Search & Sort */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-2xl mx-auto"
          >
            <div className="relative flex-1">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cari artikel berdasarkan judul..."
                className="w-full pl-12 pr-4 py-3 rounded-full bg-white border border-slate-200 shadow-soft focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none transition-all font-overpass text-slate-800 placeholder:text-slate-400"
              />
            </div>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-5 py-3 rounded-full bg-white border border-slate-200 shadow-soft focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-slate-800 cursor-pointer transition-all"
            >
              <option value="desc">Terbaru Dulu</option>
              <option value="asc">Terlama Dulu</option>
            </select>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16 w-full">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="h-80 rounded-3xl bg-slate-100 animate-pulse-soft"
              />
            ))}
          </div>
        ) : error ? (
          <div className="text-center text-red-500 py-12">{error}</div>
        ) : filteredBlogs.length === 0 ? (
          <div className="text-center text-slate-500 py-20 font-overpass">
            <div className="text-5xl mb-4">🔎</div>
            <p>Tidak ada artikel yang cocok dengan pencarian Anda.</p>
          </div>
        ) : (
          <>
            {/* Featured Post */}
            {featured && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="mb-12"
              >
                <Link
                  to={`/blog/${featured.id}`}
                  className="group grid md:grid-cols-2 gap-6 lg:gap-8 bg-white rounded-3xl overflow-hidden shadow-medium hover:shadow-strong card-hover border border-slate-100 no-underline hover:no-underline"
                >
                  <div className="relative h-64 md:h-auto overflow-hidden">
                    <img
                      src={
                        isValidUrl(featured.image)
                          ? featured.image
                          : placeholderImage
                      }
                      alt={featured.title}
                      loading="eager"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-gradient-to-r from-brand-600 to-eco-600 text-white shadow-lg">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      Featured
                    </div>
                  </div>
                  <div className="p-6 lg:p-10 flex flex-col justify-center">
                    <div className="text-xs font-semibold tracking-wider uppercase text-brand-600 mb-3 font-overpass">
                      {formatDate(featured.created_at)}
                    </div>
                    <h2 className="font-oswald text-2xl lg:text-3xl font-semibold text-slate-900 mb-4 group-hover:text-brand-700 transition-colors leading-tight">
                      {featured.title}
                    </h2>
                    <p className="font-overpass text-slate-600 text-sm lg:text-base leading-relaxed line-clamp-3 mb-5">
                      {previewText(featured.description, 200)}
                    </p>
                    <div className="flex items-center gap-3 mt-auto pt-4 border-t border-slate-100">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-eco-500 flex items-center justify-center text-white text-sm font-bold">
                        {featured.author?.charAt(0).toUpperCase() || "D"}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-800 font-overpass">
                          {featured.author}
                        </div>
                        <div className="text-xs text-slate-500 font-overpass">
                          Author
                        </div>
                      </div>
                      <div className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 group-hover:gap-2.5 transition-all">
                        Baca
                        <span>→</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            )}

            {/* Rest of blogs */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((blog, idx) => (
                <motion.div
                  key={blog.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: (idx % 6) * 0.06 }}
                >
                  <Link
                    to={`/blog/${blog.id}`}
                    className="group flex flex-col h-full bg-white rounded-3xl overflow-hidden shadow-soft hover:shadow-medium card-hover border border-slate-100 no-underline hover:no-underline"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={
                          isValidUrl(blog.image)
                            ? blog.image
                            : placeholderImage
                        }
                        alt={blog.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="flex-1 p-5 flex flex-col">
                      <div className="text-xs font-semibold tracking-wider uppercase text-brand-600 mb-2 font-overpass">
                        {formatDate(blog.created_at)}
                      </div>
                      <h3 className="font-oswald text-lg font-semibold text-slate-900 group-hover:text-brand-700 transition-colors leading-snug line-clamp-2 mb-2">
                        {blog.title}
                      </h3>
                      <p className="font-overpass text-sm text-slate-600 leading-relaxed line-clamp-3 mb-4">
                        {previewText(blog.description, 120)}
                      </p>
                      <div className="flex items-center gap-2 mt-auto pt-3 border-t border-slate-100 text-xs font-overpass">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-500 to-eco-500 flex items-center justify-center text-white text-xs font-bold">
                          {blog.author?.charAt(0).toUpperCase() || "D"}
                        </div>
                        <span className="font-medium text-slate-700">
                          {blog.author}
                        </span>
                        <span className="ml-auto text-brand-600 font-semibold group-hover:translate-x-1 transition-transform">
                          →
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default BlogsListPage;
